import helper from './helper';
import { expr2expr } from './alphabet';

class Rows {
  constructor({ len, height, minHeight }) {
    this._ = {};
    this.len = len;
    // default row height
    this.height = height;
    this.minHeight = minHeight;
  }

  getHeight(ri) {
    if (this.isHide(ri)) return 0;
    const row = this.get(ri);
    if (row && row.height) {
      return row.height;
    }
    return this.height;
  }

  setHeight(ri, v) {
    const row = this.getOrNew(ri);
    row.height = v;
  }

  unhide(idx) {
    let index = idx;
    while (index > 0) {
      index -= 1;
      if (this.isHide(index)) {
        this.setHide(index, false);
      } else break;
    }
  }

  isHide(ri) {
    const row = this.get(ri);
    return row && row.hide;
  }

  setHide(ri, v) {
    const row = this.getOrNew(ri);
    if (v === true) row.hide = true;
    else delete row.hide;
  }

  setStyle(ri, style) {
    const row = this.getOrNew(ri);
    row.style = style;
  }

  sumHeight(min, max, exceptSet) {
    return helper.rangeSum(min, max, (i) => {
      if (exceptSet && exceptSet.has(i)) return 0;
      return this.getHeight(i);
    });
  }

  totalHeight() {
    return this.sumHeight(0, this.len);
  }

  get(ri) {
    return this._[ri];
  }

  getOrNew(ri) {
    this._[ri] = this._[ri] || { cells: {} };
    return this._[ri];
  }

  getCell(ri, ci) {
    const row = this.get(ri);
    if (row !== undefined && row.cells !== undefined && row.cells[ci] !== undefined) {
      return row.cells[ci];
    }
    return null;
  }

  getCellMerge(ri, ci) {
    const cell = this.getCell(ri, ci);
    if (cell && cell.merge) return cell.merge;
    return [0, 0];
  }

  getCellOrNew(ri, ci) {
    const row = this.getOrNew(ri);
    if (!row.cells) {
        row.cells = {}
    }
    row.cells[ci] = row.cells[ci] || {};
    return row.cells[ci];
  }

  // what: all | text | format
  setCell(ri, ci, cell, what = 'all') {
    const row = this.getOrNew(ri);
    if (!row.cells) {
        row.cells = {}
    }
    if (what === 'all') {
      row.cells[ci] = cell;
    } else if (what === 'text') {
      row.cells[ci] = row.cells[ci] || {};
      row.cells[ci].text = cell.text;
    } else if (what === 'format') {
      row.cells[ci] = row.cells[ci] || {};
      if (cell.style != undefined) {
        row.cells[ci].style = cell.style;
      } else {
        delete row.cells[ci].style;
      }
      if (cell.merge) row.cells[ci].merge = cell.merge;
      if (Object.keys(row.cells[ci]).length == 0) {
        delete row.cells[ci];
      }
    }
    if (row.cells && Object.keys(row.cells).length == 0) {
      delete row.cells;
    }
    if (Object.keys(row).length == 0) {
      delete this._[ri];
    }
  }

  setCellText(ri, ci, text) {
    const cell = this.getCellOrNew(ri, ci);
    if (cell.editable !== false) {
        cell.text = text;
    }
  }

  setCellProperty(ri, ci, key, value) {
    const cell = this.getCellOrNew(ri, ci);
    if (cell.editable !== false) {
        cell[key] = value;
    }
  }

  // what: all | format | text
  copyPaste(srcCellRange, dstCellRange, what, autofill = false, cb = () => {}) {
    const {
      sri, sci, eri, eci,
    } = srcCellRange;
    const dsri = dstCellRange.sri;
    const dsci = dstCellRange.sci;
    const deri = dstCellRange.eri;
    const deci = dstCellRange.eci;
    const [rn, cn] = srcCellRange.size();
    const [drn, dcn] = dstCellRange.size();
    // console.log(srcIndexes, dstIndexes);
    let isAdd = true;
    let dn = 0;
    if (deri < sri || deci < sci) {
      isAdd = false;
      if (deri < sri) dn = drn;
      else dn = dcn;
    }
    for (let i = sri; i <= eri; i += 1) {
      if (this._[i]) {
        for (let j = sci; j <= eci; j += 1) {
          if (this._[i].cells && this._[i].cells[j]) {
            for (let ii = dsri; ii <= deri; ii += rn) {
              for (let jj = dsci; jj <= deci; jj += cn) {
                const nri = ii + (i - sri);
                const nci = jj + (j - sci);
                const ncell = helper.cloneDeep(this._[i].cells[j]);
                // ncell.text
                if (autofill && ncell && ncell.text && ncell.text.length > 0) {
                  const { text } = ncell;
                  let n = (jj - dsci) + (ii - dsri) + 2;
                  if (!isAdd) {
                    n -= dn + 1;
                  }
                  if (text[0] === '=') {
                    ncell.text = text.replace(/[a-zA-Z]{1,3}\d+/g, (word) => {
                      let [xn, yn] = [0, 0];
                      if (sri === dsri) {
                        xn = n - 1;
                        // if (isAdd) xn -= 1;
                      } else {
                        yn = n - 1;
                      }
                      if (/^\d+$/.test(word)) return word;
                      return expr2expr(word, xn, yn);
                    });
                  } else if ((rn <= 1 && cn > 1 && (dsri > eri || deri < sri))
                    || (cn <= 1 && rn > 1 && (dsci > eci || deci < sci))
                    || (rn <= 1 && cn <= 1)) {
                    const result = /[\\.\d]+$/.exec(text);
                    // console.log('result:', result);
                    if (result !== null) {
                      const index = Number(result[0]) + n - 1;
                      ncell.text = text.substring(0, result.index) + index;
                    }
                  }
                }
                this.setCell(nri, nci, ncell, what);
                cb(nri, nci, ncell);
              }
            }
          }
        }
      }
    }
  }

  cutPaste(srcCellRange, dstCellRange) {
    const ncellmm = {};
    this.each((ri) => {
      this.eachCells(ri, (ci) => {
        let nri = parseInt(ri, 10);
        let nci = parseInt(ci, 10);
        if (srcCellRange.includes(ri, ci)) {
          nri = dstCellRange.sri + (nri - srcCellRange.sri);
          nci = dstCellRange.sci + (nci - srcCellRange.sci);
        }
        ncellmm[nri] = ncellmm[nri] || { cells: {} };
        ncellmm[nri].cells[nci] = this._[ri].cells[ci];
      });
    });
    this._ = ncellmm;
  }

  // src: Array<Array<String>>
  paste(src, dstCellRange) {
    if (src.length <= 0) return;
    const { sri, sci } = dstCellRange;
    src.forEach((row, i) => {
      const ri = sri + i;
      row.forEach((cell, j) => {
        const ci = sci + j;
        this.setCellText(ri, ci, cell);
      });
    });
  }

  insert(sri, n = 1) {
    const ndata = {};
    this.each((ri, row) => {
      let nri = parseInt(ri, 10);
      if (nri >= sri) {
        nri += n;
        this.eachCells(ri, (ci, cell) => {
          if (cell.text && cell.text[0] === '=') {
            cell.text = cell.text.replace(/[a-zA-Z]{1,3}\d+/g, word => expr2expr(word, 0, n, (x, y) => y >= sri));
          }
        });
      }
      ndata[nri] = row;
    });
    this._ = ndata;
    this.len += n;
  }

  delete(sri, eri) {
    const n = eri - sri + 1;
    const ndata = {};
    this.each((ri, row) => {
      const nri = parseInt(ri, 10);
      if (nri < sri) {
        ndata[nri] = row;
      } else if (ri > eri) {
        ndata[nri - n] = row;
        this.eachCells(ri, (ci, cell) => {
          if (cell.text && cell.text[0] === '=') {
            cell.text = cell.text.replace(/[a-zA-Z]{1,3}\d+/g, word => expr2expr(word, 0, -n, (x, y) => y > eri));
          }
        });
      }
    });
    this._ = ndata;
    this.len -= n;
  }

  insertColumn(sci, n = 1) {
    this.each((ri, row) => {
      const rndata = {};
      this.eachCells(ri, (ci, cell) => {
        let nci = parseInt(ci, 10);
        if (nci >= sci) {
          nci += n;
          if (cell.text && cell.text[0] === '=') {
            cell.text = cell.text.replace(/[a-zA-Z]{1,3}\d+/g, word => expr2expr(word, n, 0, x => x >= sci));
          }
        }
        rndata[nci] = cell;
      });
      row.cells = rndata;
    });
  }

  deleteColumn(sci, eci) {
    const n = eci - sci + 1;
    this.each((ri, row) => {
      const rndata = {};
      this.eachCells(ri, (ci, cell) => {
        const nci = parseInt(ci, 10);
        if (nci < sci) {
          rndata[nci] = cell;
        } else if (nci > eci) {
          rndata[nci - n] = cell;
          if (cell.text && cell.text[0] === '=') {
            cell.text = cell.text.replace(/[a-zA-Z]{1,3}\d+/g, word => expr2expr(word, -n, 0, x => x > eci));
          }
        }
      });
      row.cells = rndata;
    });
  }

  // what: all | text | format | merge
  deleteCells(cellRange, what = 'all') {
    cellRange.each((i, j) => {
      this.deleteCell(i, j, what);
    });
  }

  // what: all | text | format | merge
  deleteCell(ri, ci, what = 'all') {
    const row = this.get(ri);
    if (row !== null) {
      const cell = this.getCell(ri, ci);
      if (cell !== null && cell.editable !== false) {
        if (what === 'all') {
          delete row.cells[ci];
        } else if (what === 'text') {
          if (cell.type) delete cell.type;
          if (cell.text) delete cell.text;
          if (cell.value) delete cell.value;
          if (cell.url != undefined) delete cell.url;
          if (cell.imageScale != undefined) delete cell.imageScale;
          if (cell.defaultValue) delete cell.defaultValue;
          if (cell.dataType) delete cell.dataType;
          if (cell.splitString) delete cell.splitString;
          if (cell.cross) delete cell.cross;
          if (cell.expandDirection) delete cell.expandDirection;
          if (cell.expandType) delete cell.expandType;
          if (cell.group) delete cell.group;
          if (cell.formula) delete cell.formula;
        } else if (what === 'format') {
          if (cell.style !== undefined) delete cell.style;
          if (cell.merge) delete cell.merge;
        } else if (what === 'merge') {
          if (cell.merge) delete cell.merge;
        }
        if (Object.keys(cell).length == 0) {
          delete row.cells[ci]
        }
      }
      if (row && row.cells && Object.keys(row.cells).length == 0) {
        delete row.cells;
      }
      if (row && Object.keys(row).length == 0) {
        delete this._[ri];
      }
    }
  }

  maxCell() {
    let ri = 0;
    let ci = 0;
    const keys = Object.keys(this._);
    for (let i = 0; i < keys.length; i++) {
      const ri1 = keys[i];
      const row = this._[ri1];
      if (row) {
        const { cells } = row;
        if (cells) {
            const ks = Object.keys(cells);
            const ci1 = ks[ks.length - 1];
            const ri2 = parseInt(ri1 || '0', 10)
            const ci2 = parseInt(ci1 || '0', 10)
            if (ri2 > ri) {
                ri = ri2;
            }
            if (ci2 > ci) {
                ci = ci2;
            }
        }
      }
    }
    
    return [ri, ci];
  }

  maxCellWithMerge() {
    let ri = 0;
    let ci = 0;
    const keys = Object.keys(this._);
    for (let i = 0; i < keys.length; i++) {
      const ri1 = keys[i];
      const row = this._[ri1];
      if (row) {
        const { cells } = row;
        if (cells) {
            const ks = Object.keys(cells);
            const ci1 = ks[ks.length - 1];
            const ri2 = parseInt(ri1 || '0', 10)
            const ci2 = parseInt(ci1 || '0', 10)
            if (ri2 > ri) {
                ri = ri2;
            }
            if (ci2 > ci) {
                ci = ci2;
            }

            const cell = cells[ci1]
            if (cell && cell.merge && cell.merge.length > 0) {
                if (ri2 + cell.merge[0] > ri) {
                    ri = ri2 + cell.merge[0]
                }
                if (ci2 + cell.merge[1] > ci) {
                    ci = ci2 + cell.merge[1]
                }
            }
        }
      }
    }
    
    return [ri, ci];
  }

  each(cb) {
    Object.entries(this._).forEach(([ri, row]) => {
      cb(ri, row);
    });
  }

  eachCells(ri, cb) {
    if (this._[ri] && this._[ri].cells) {
      Object.entries(this._[ri].cells).forEach(([ci, cell]) => {
        cb(ci, cell);
      });
    }
  }

  setData(d) {
    if (d.len) {
      this.len = d.len;
      delete d.len;
    }
    if (d.height) {
        this.height = d.height;
        delete d.height;
    }
    this._ = d;
  }

  getData() {
    const { len, height } = this;
    return Object.assign({ len, height }, this._);
  }

  getAllStyles() {
    const styles = {}
    const keys = Object.keys(this._);
    for (let i = 0; i < keys.length; i++) {
      const ri = keys[i];
      const row = this._[ri];
      if (row) {
        const { cells } = row;
        if (cells) {
            const ckeys = Object.keys(cells);
            for (let j = 0; j < ckeys.length; j++) {
                const cell = cells[ckeys[j]];
                if (cell.style != undefined) {
                    styles[cell.style] = cell.style;
                }
            }
        }
      }
    }
    return styles;
  }
}

export default {};
export {
  Rows,
};
